// I actually just move the existing code from accounts.js to this file
// as attempt to reduce the size of accounts.js and make Sonar compliant with it
import { isSearchEnabled } from './search_settings';
import {
  accountInfoFields,
  accountOrgInfoFields,
  addCopyButton,
  addLoadAccountDetailsButton,
  addLoadOrderDetailsButton,
  checkValueAgainstList,
  checkIfAnyInList,
  configureTableColumns,
  createAndAddInfoTable,
  encodeEntities,
  retrieveVisibleFields,
  rowDetailsClickHandler,
  showErrorFromApiOperation,
  displaySuccessPopup,
  createSwitchIdcRoleLink,
  ssoStartUrls,
} from './main';
import { accountIdPattern, updateAccountSummary, resetAccountSummary } from './accounts';
import { addSpinner, addTableSpinner, removeSpinners } from './sidebar';
import { baseApiAxios, microservicesApiAxios, showError } from './api';
import { getCurrentUserName } from './auth';
import { checkResponsibility } from './dashboard';
import { CONF } from './environment';
import { fetchAccountAccessLoginDetails, restoreIdcSignInLinks } from './account_access_login';
import AccountTabs from '../../jsx/components/forms/AccountTabs';
import { initDataTable, exportButtons, resetTableButton, reloadTableButton, col, selectColumn } from './datatable';

const accountsDataTableButtonAction = (action, text, titleAttribute) => {
  let endpoint = 'invalid';
  switch (action) {
    case 'organizations-update':
      endpoint = '/organizations/trigger-update';
      break;
    case 'accounts-update':
      endpoint = '/accounts/trigger-update';
      break;
  }
  return {
    text: text,
    action: () => {
      addSpinner();
      baseApiAxios
        .post(endpoint)
        .then(response => {
          displaySuccessPopup(response.message, 'Your request was successful.');
        })
        .catch(showError)
        .finally(removeSpinners);
    },
    titleAttr: titleAttribute,
  };
};

const canViewToggles = () => {
  return (
    checkValueAgainstList('manage_accounts', localStorage.permissions) ||
    checkValueAgainstList('view_all_accounts', localStorage.permissions) ||
    localStorage.account_areas
  );
};

const canViewCosts = () => {
  return checkIfAnyInList(['manage_accounts', 'manage_organizations'], localStorage.permissions?.split(',') || []);
};

const sumColumnValues = (a, b) => {
  const removeFormatting = value => {
    const typeOfValue = typeof value;
    return typeOfValue === 'string' ? value.replace(/[$,]|[CN¥,]/g, '') * 1 : typeOfValue === 'number' ? value : 0;
  };

  const sanitizedA = a === '-' ? 0 : a;
  const sanitizedB = b === '-' ? 0 : b;

  return removeFormatting(sanitizedA) + removeFormatting(sanitizedB);
};

// list accounts table

const baseAccountsTableColumns = [
  { id: 'select' },
  { id: 'region_category', name: 'Region Category' },
  { id: 'master_account', name: 'Master Account ID' },
  { id: 'account_id', name: 'Account ID' },
  { id: 'account_root_email', name: 'Root Account' },
  { id: 'creation_date', name: 'Creation Date' },
  { id: 'fpc_status', name: 'FPC Status' },
  { id: 'account_friendly_name', name: 'Account Friendly Name' },
  { id: 'description', name: 'Description' },
  { id: 'account_area', name: 'Account Area' },
  { id: 'account_type', name: 'Account Type' },
  { id: 'account_stage', name: 'Account Stage' },
  { id: 'it_responsible', name: 'IT Responsible' },
  { id: 'cost_data', name: 'App. Service Managed by group' },
  { id: 'support_plan', name: 'AWS Support Plan' },
  { id: 'cost_data', name: 'Business Service ID' },
  { id: 'cost_data', name: 'Application Service ID' },
  { id: 'cost_data', name: 'Cost Center' },
  { id: 'cost_data', name: 'PSO/PSP' },
  { id: 'cdh_user_roles', name: 'CDH accessible roles (decentral)' },
  { id: 'idc_user_roles', name: 'IDC accessible roles (central)' },
  { id: 'org_id', name: 'Org Id' },
  { id: 'org_name', name: 'Org Name' },
  { id: 'org_ou_id', name: 'Org Unit Id' },
  { id: 'org_ou_name', name: 'Org Unit Name' },
  { id: 'org_status', name: 'Org Status' },
  { id: 'org_status_update', name: 'Last Org Update (UTC)' },
  { id: 'access_status', name: 'Access Check' },
  { id: 'access_update', name: 'Last Access Update (UTC)' },
  { id: 'cost_data', name: 'App Criticality' },
  { id: 'cost_data', name: 'ITSM Service' },
  { id: 'tags', name: 'Tag: Managed By' },
  { id: 'tags', name: 'Tag: Managed By Platform' },
  { id: 'tags', name: 'Tag: Consumed By' },
  { id: 'tags', name: 'Tag: Provisioned By' },
  { id: 'tags', name: 'Tag: Secured By' },
  { id: 'tags', name: 'Tag: APPD-ID' },
  { id: 'tags', name: 'Tag: Application Service ID' },
];

const accountsTableCostsColumns = [
  { id: 'month_2', name: 'Costs two months ago' },
  { id: 'month_1', name: 'Costs last month' },
  { id: 'month_0', name: 'Costs current month' },
];

const actionsColumn = [{ id: 'actions', name: 'Actions' }];

// Build the full columns array at runtime so that the cost columns are
// included when permissions are already available in localStorage.
function getAccountsTableColumns() {
  let cols = [...baseAccountsTableColumns];
  if (canViewCosts()) cols = cols.concat(accountsTableCostsColumns);
  cols = cols.concat(actionsColumn);
  return cols;
}

const dropdownColumns = [
  'region_category',
  'master_account',
  'fpc_status',
  'account_area',
  'account_type',
  'account_stage',
  'org_id',
  'org_name',
  'org_ou_id',
  'org_ou_name',
  'org_status',
  'access_status',
  'support_plan',
  'app_criticality',
  'itsm_service',
];

const searchColumns = [
  'account_id',
  'account_root_email',
  'creation_date',
  'account_friendly_name',
  'description',
  'it_responsible',
  'ops_responsibles',
  'business_service_id',
  'application_service_id',
  'cost_center',
  'wbs',
  'org_status_update',
  'access_update',
  'tag_managed_by',
  'tag_managed_by_platform',
  'tag_consumed_by',
  'tag_provisioned_by',
  'tag_secured_by',
  'tag_appd_id',
  'tag_application_service_id',
];

function responsibleUserCell(td, cellData) {
  addCopyButton(td);
  const currentUser = getCurrentUserName().toLowerCase();
  if (cellData && typeof cellData === 'string') {
    $(td).addClass(currentUser == cellData?.toLowerCase() ? 'current-user-responsible' : '');
  }
}

const accountsDataTableColumns = () => {
  let accountsColumns = [
    // Column 0
    selectColumn({ name: 'select', width: '20px' }),
    // Column 1
    col('region_category', 'region_category', 'Region Category', {
      visible: false,
      createdCell: function (td, cellData) {
        addCopyButton(td);
        if (cellData === 'China') {
          $(td).addClass('portal-warning');
        }
      },
    }),
    // Column 2
    col('master_account', 'master_account', 'Master Account Id', {
      visible: false,
      createdCell: function (td, cellData) {
        addCopyButton(td);
        addLoadAccountDetailsButton(td);
        if (!accountIdPattern.test(cellData)) {
          $(td).addClass('portal-danger');
        }
      },
    }),
    // Column 3
    col('account_id', 'account_id', 'Account Id', {
      createdCell: function (td, cellData, rowData) {
        if (!accountIdPattern.test(cellData)) {
          $(td).addClass('portal-danger');
        } else if (checkResponsibility(rowData, getCurrentUserName())) {
          $(td).addClass('bold-text');
        }
        addCopyButton(td);
        addLoadAccountDetailsButton(td);
      },
    }),
    // Column 4
    col('account_root_email', 'account_root_email', 'Root Account', { visible: false }),
    // Column 5
    col('creation_date', 'creation_date', 'Creation Date', { visible: false }),
    // Column 6
    col('fpc_status', 'fpc_status', 'FPC Status', { visible: false }),
    // Column 7
    col('account_friendly_name', 'account_friendly_name', 'Account Friendly Name'),
    // Column 8
    col('description', 'description', 'Description', { visible: false }),
    // Column 9
    col('account_area', 'account_area', 'Account Area'),
    // Column 10
    col('account_type', 'account_type', 'Account Type'),
    // Column 11
    col('account_stage', 'account_stage', 'Account Stage', {
      createdCell: function (td, cellData) {
        addCopyButton(td);
        if (cellData === 'basic') {
          $(td).addClass('portal-danger');
        }
      },
    }),
    // Column 12
    col('it_responsible', 'it_responsible', 'IT Responsible', {
      visible: false,
      createdCell: responsibleUserCell,
    }),
    // Column 15
    col('ops_responsibles', 'ops_responsibles', 'App. Service Managed by group', {
      visible: false,
      createdCell: function (td, cellData) {
        if (Array.isArray(cellData) && cellData.length > 0) {
          const currentUser = getCurrentUserName().toLowerCase();
          const userIDs = cellData.map((data, index) => (
            <span className={currentUser == data.toLowerCase() ? 'current-user-responsible' : ''}>
              {data}
              {index < cellData.length - 1 ? ', ' : ''}
            </span>
          ));
          $(td).html(userIDs).css('min-width', '400px').css('white-space', 'normal').css('word-break', 'break-word');
        }
      },
    }),
    // Column 16
    col('support_plan', 'support_plan', 'AWS Support Plan', { visible: false, searchable: false }),
    // Column 17
    col('business_service_id', 'business_service_id', 'Business Service ID', {
      visible: false,
      createdCell: function (td, cellData) {
        if (!cellData) {
          $(td).text('unknown');
          $(td).addClass('portal-danger');
        }
        addCopyButton(td);
        const appid_pattern = /^APP-\d{4,7}$/;
        if (!appid_pattern.test(cellData)) {
          $(td).addClass('portal-danger');
        }
      },
    }),
    // Column 18
    col('application_service_id', 'application_service_id', 'Application Service ID', {
      visible: false,
      createdCell: function (td, cellData) {
        if (!cellData) {
          $(td).text('unknown');
          $(td).addClass('portal-danger');
        }
        addCopyButton(td);
        const appdid_pattern = /^APPD-\d+$/;
        if (!appdid_pattern.test(cellData)) {
          $(td).addClass('portal-danger');
        }
      },
    }),
    // Column 19
    col('cost_center', 'cost_center', 'Cost Center', {
      visible: false,
      createdCell: function (td, cellData) {
        if (!cellData) $(td).text('unknown');
        addCopyButton(td);
      },
    }),
    // Column 20
    col('wbs', 'wbs', 'WBS', {
      visible: false,
      createdCell: function (td, cellData) {
        if (!cellData) $(td).text('unknown');
        addCopyButton(td);
      },
    }),
    // Column 21
    col(null, 'cdh_user_roles', 'CDH accessible roles (decentral)', {
      visible: false,
      defaultContent: 0,
      className: 'text-align-left',
      render: function (data) {
        if ('cdh_user_roles' in data) {
          return Array.isArray(data['cdh_user_roles']) ? data['cdh_user_roles'].length : data['cdh_user_roles'];
        }
      },
    }),
    // Column 22
    col(null, 'idc_user_roles', 'IDC accessible roles (central)', {
      visible: false,
      defaultContent: 0,
      className: 'text-align-left',
      render: function (data) {
        if ('idc_user_roles' in data) {
          return Array.isArray(data['idc_user_roles']) ? data['idc_user_roles'].length : data['idc_user_roles'];
        }
      },
    }),
    // Column 23
    col('org_id', 'org_id', 'Org Id', { visible: false }),
    // Column 24
    col('org_name', 'org_name', 'Org Name', { visible: false }),
    // Column 25
    col('org_ou_id', 'org_ou_id', 'Org Unit Id', { visible: false }),
    // Column 26
    col('org_ou_name', 'org_ou_name', 'Org Unit Name', { visible: false }),
    // Column 27
    col('org_status', 'org_status', 'Org Status', {
      visible: false,
      createdCell: function (td, cellData) {
        addCopyButton(td);
        if (cellData === 'ACTIVE') {
          $(td).addClass('portal-success');
        } else {
          $(td).addClass('portal-neutral');
        }
      },
    }),
    // Column 28
    col('org_status_update', 'org_status_update', 'Last Org Update (UTC)', { visible: false }),
    // Column 29
    col('access_status', 'access_status', 'Access Check', {
      visible: false,
      createdCell: function (td, cellData) {
        addCopyButton(td);
        if (cellData === 'Success') {
          $(td).addClass('portal-success');
        } else {
          $(td).addClass('portal-danger');
        }
      },
    }),
    // Column 30
    col('access_update', 'access_update', 'Last Access Update (UTC)', { visible: false }),
    // Column 31
    col('application_service_criticality', 'application_service_criticality', 'App Service Criticality', {
      visible: false,
    }),
    // Column 32
    col('service_offering', 'service_offering', 'Service Offering', {
      visible: false,
      createdCell: function (td, cellData) {
        $(td).text(cellData);
      },
    }),
    // Column 33
    col('tag_managed_by', 'tag_managed_by', 'Tag: Managed By', { visible: false }),
    // Column 34
    col('tag_managed_by_platform', 'tag_managed_by_platform', 'Tag: Managed By Platform', { visible: false }),
    // Column 35
    col('tag_consumed_by', 'tag_consumed_by', 'Tag: Consumed By', { visible: false }),
    // Column 36
    col('tag_provisioned_by', 'tag_provisioned_by', 'Tag: Provisioned By', { visible: false }),
    // Column 37
    col('tag_secured_by', 'tag_secured_by', 'Tag: Secured By', { visible: false }),
    // Column 38
    col('tag_appd_id', 'tag_appd_id', 'Tag: APPD-ID', { visible: false }),
    // Column 39
    col('tag_application_service_id', 'tag_application_service_id', 'Tag: Application Service ID', { visible: false }),
  ];
  if (canViewCosts()) {
    accountsColumns = accountsColumns.concat([
      // Column 33
      col('month_2', 'month_2', 'Costs two months ago', { visible: false, className: 'text-align-left' }),
      // Column 34
      col('month_1', 'month_1', 'Costs last month', { visible: false, className: 'text-align-left' }),
      // Column 35
      col('month_0', 'month_0', 'Costs current month', { visible: false, className: 'text-align-left' }),
    ]);
  }
  accountsColumns = accountsColumns.concat([
    // Column 36
    {
      visible: true,
      defaultContent: '',
      orderable: false,
      searchable: false,
      data: null,
      name: 'actions',
      title: 'Actions',
      class: 'details-edit',
      width: '50px',
      createdCell: createActionsButtons,
    },
  ]);

  return accountsColumns;
};

let showAllAccounts = false;
let showDeletedAccounts = false;
/*For the list view we always want to show quarantined accounts (which will have an different colored font).
And we don't have a user-story/request to have a switch toggle to show/hide quarantined accounts, so the default will be always-show.
If we have a user-story in the future to implement a toggle for "show/hide quarantined accounts,...then we simply attach an event handler that will update this variable just like we've done for deleted accounts*/
let showQuarantinedAccounts = true;

const flattenAccountTags = account => {
  if (!account.tags || typeof account.tags !== 'object') return {};
  return Object.fromEntries(
    Object.entries(account.tags).map(([key, value]) => ['tag_' + key.toLowerCase().replace(/[^a-z0-9_]/g, '_'), value]),
  );
};

async function loadAccountsData(tableId, headers, forceReload) {
  // First we must clear the table.
  const table = $('#table-accounts');
  const dataTable = table.DataTable();
  dataTable.clear();
  resetAccountSummary();
  $('#show-all-accounts-switch').attr('disabled', true);
  $('#show-deleted-accounts-switch').attr('disabled', true);

  if (forceReload) {
    headers = { ...headers, 'Cache-Control': 'max-age=0, must-revalidate' };
  }

  const attributes = retrieveVisibleFields('#' + tableId, getAccountsTableColumns());

  if (attributes.indexOf('fpc_status') === -1) attributes.push('fpc_status');
  if (attributes.indexOf('account_id') === -1) attributes.push('account_id');
  /* Even if the user does not want account_area to be a visible column in the UI, we still have to fetch it from the backend API.
   This is because now that we have an account area "quarantine" with a different visual indicator , we need this field in order to be able to update the visual indicator (color) in the UI */
  if (attributes.indexOf('account_area') === -1) attributes.push('account_area');
  // We still need the responsible fields even if it's not displayed in the UI by choice of the user. This is because of a new
  // requirement to somehow highlight an account in which the current user is responsible for.
  if (attributes.indexOf('it_responsible') === -1) attributes.push('it_responsible');
  if (attributes.indexOf('cost_data') === -1) attributes.push('cost_data');
  if (attributes.indexOf('tags') === -1) attributes.push('tags');

  let useOpenSearch = isSearchEnabled('account_search_enable');
  let retryCount = 0;
  const maxRetries = 1; // Allow one retry with different search method
  const params = {
    attribute_names: attributes.join(),
    fields: attributes.join(),
    all: showAllAccounts,
    deleted: showDeletedAccounts,
    quarantined: showQuarantinedAccounts,
    limit: 1000,
    size: 1000,
  };

  while (retryCount <= maxRetries) {
    try {
      addSpinner(false);
      addTableSpinner();

      console.log(`account_search_enable: ${useOpenSearch}`);
      let { items, next_token } = useOpenSearch
        ? await microservicesApiAxios.getSearchAccounts(headers, params)
        : await baseApiAxios.getAccounts(headers, params);
      items = items.map(account => ({
        ...account,
        ...flattenAccountTags(account),
        ops_responsibles: account.ops_responsibles
          ? account.ops_responsibles.map(responsible => responsible.user_id)
          : [],
      }));
      addAccountRows(dataTable, items);
      updateAccountSummary(items);

      while (next_token) {
        // As long as there are still more records to evaluate, we keep calling the api and loading the dataTable.
        params['next_token'] = next_token;
        ({ items, next_token } = useOpenSearch
          ? await microservicesApiAxios.getSearchAccounts(headers, params)
          : await baseApiAxios.getAccounts(headers, params));

        items = items.map(item => ({
          ...item,
          ...flattenAccountTags(item),
          ops_responsibles: item.ops_responsibles ? item.ops_responsibles.map(responsible => responsible.user_id) : [],
        }));
        addSpinner(false);
        addTableSpinner();
        addAccountRows(dataTable, items);
        updateAccountSummary(items);
      }

      $(window).trigger('resize');
      break;
    } catch (e) {
      dataTable.clear();
      if (useOpenSearch && retryCount < maxRetries) {
        retryCount++;
        useOpenSearch = false;
        sessionStorage.setItem('account_search_enable', 'false');
        console.warn('Failed to query openSearch, querying DB instead.');
      } else {
        showErrorFromApiOperation('Error fetching list of accounts')(e);
        break;
      }
    }
  }
  dataTable.draw();
  rebuildAccountTableFooter();
  removeSpinners();
  $('#show-all-accounts-switch').attr('disabled', false);
  $('#show-deleted-accounts-switch').attr('disabled', false);
}

/**
 * Adds rows to the accounts DataTable without drawing or rebuilding footers.
 * Call this per-batch during paginated loading for maximum speed.
 *
 * @param {Object} dataTable - DataTable API instance
 * @param {Array} dataArray - Array of account data objects
 */
const addAccountRows = (dataTable, dataArray) => {
  if (!dataArray || !Array.isArray(dataArray)) {
    hideTableAndShowAlert();
    console.warn('Not a list');
    removeSpinners();
    return;
  }

  dataTable.rows.add(dataArray);
};

/**
 * Rebuilds the footer dropdowns and search inputs for the accounts table.
 * Call this once after all data has been loaded and drawn.
 */
const rebuildAccountTableFooter = () => {
  const table = $('#table-accounts');
  const dataTable = table.DataTable();
  const dataTableApi = table.dataTable();
  const columnsState = dataTableApi.api().state().columns;

  dataTable.columns().every(function (columnIndex) {
    const column = this;
    const dataSource = column.dataSrc();
    const that = this;
    const title = column.header().textContent;

    if (dropdownColumns.indexOf(dataSource) > -1) {
      const select = $('<select><option value=""></option></select>')
        .appendTo($(column.footer()).empty())
        .on('change', function () {
          const val = $.fn.dataTable.util.escapeRegex($(this).val());
          column.search(val ? '^' + val + '$' : '', true, false).draw();
        });

      column
        .data()
        .unique()
        .sort()
        .each(function (data) {
          let opt = document.createElement('option');
          opt.innerText = data;
          opt.value = data;
          select.append(opt);

          if (
            data &&
            columnsState[columnIndex].search &&
            columnsState[columnIndex].search.search &&
            data.match(columnsState[columnIndex].search.search)
          ) {
            opt.selected = true;
          }
        });
    }

    if (searchColumns.indexOf(dataSource) > -1) {
      $(column.footer()).html('<input type="text" placeholder="Search ' + title + '" />');

      if (columnsState[columnIndex].search && columnsState[columnIndex].search.search) {
        column.footer().lastChild.value = columnsState[columnIndex].search.search;
      }

      $('input', that.footer()).on('keyup change', function () {
        if (that.search() !== this.value) {
          that.search(this.value).draw();
        }
      });
    }
  });
};

const hideTableAndShowAlert = () => {
  $('#table-accounts').hide();
  $('#table-dropdown-button').hide();
  $('#noaccountalert').show();
};

const accountsDataTableToggles = tableId => {
  $('#show-all-accounts-div').toggleClass('d-none', !canViewToggles());

  $('#show-all-accounts-switch')
    .attr('checked', showAllAccounts)
    .on('change', function () {
      if (showAllAccounts !== this.checked) {
        showAllAccounts = this.checked;
        loadAccountsData(tableId);
      }
    });

  $('#show-deleted-accounts-switch')
    .attr('checked', showDeletedAccounts)
    .on('change', function () {
      if (showDeletedAccounts !== this.checked) {
        showDeletedAccounts = this.checked;
        loadAccountsData(tableId);
      }
    });
};

const accountsDataTableFooterButtons = () => {
  return [
    ...exportButtons(),
    resetTableButton(),
    reloadTableButton('Reload Accounts', loadAccountsData, 'Reload AWS accounts (no-cache)'),
  ];
};

export async function initAccountTable(tableId) {
  configureTableColumns(tableId, getAccountsTableColumns());

  const formatter = new Intl.NumberFormat(CONF.language, {
    style: 'currency',
    currency: CONF.currency,
    maximumFractionDigits: 2,
  });

  const cols = accountsDataTableColumns();
  const costsTargets = canViewCosts()
    ? ['month_2', 'month_1', 'month_0'].map(n => cols.findIndex(c => c.name === n)).filter(i => i >= 0)
    : [];

  initDataTable(
    tableId,
    accountsDataTableFooterButtons(),
    cols,
    function (row, data) {
      if (data.fpc_status === 'deleted') {
        $(row).addClass('row-deleted');
      } else if (data.fpc_status === 'halt') {
        $(row).addClass('row-inactive');
      }
      if (data.account_area && data.account_area.toLowerCase() === 'quarantine') {
        // The check for account_area existence is just an extra guard. I know all accounts should have an account_area, but please don't remove this.
        $(row).addClass('row-quarantined');
      }
    },
    {
      columnDefs: [
        {
          targets: costsTargets,
          render: function (data) {
            if (!data || data === true) {
              return '-';
            } else {
              return formatter.format(parseFloat(data));
            }
          },
        },
        {
          targets: '_all',
          render: {
            display: encodeEntities,
          },
        },
      ],
      order: [[3, 'asc']],
      footerCallback: function (_row, _data, _start, _end, _display) {
        const api = this.api();

        // Guard: only compute cost totals if the cost columns were actually
        // added to the table (they are appended at module-load time and may
        // be missing on the very first page load when permissions haven't
        // been written to localStorage yet).
        if (!canViewCosts()) return;

        try {
          // Total over all pages
          const total = api.column('month_0:name').data().reduce(sumColumnValues, 0);
          const total_1 = api.column('month_1:name').data().reduce(sumColumnValues, 0);
          const total_2 = api.column('month_2:name').data().reduce(sumColumnValues, 0);
          // Total over this page
          const pageTotal = api.column('month_0:name', { page: 'current' }).data().reduce(sumColumnValues, 0);
          const pageTotal_1 = api.column('month_1:name', { page: 'current' }).data().reduce(sumColumnValues, 0);
          const pageTotal_2 = api.column('month_2:name', { page: 'current' }).data().reduce(sumColumnValues, 0);

          // Update footer
          $(api.column('month_0:name').footer()).html(
            formatter.format(pageTotal) + ' (' + formatter.format(total) + ' total)',
          );
          $(api.column('month_1:name').footer()).html(
            formatter.format(pageTotal_1) + ' (' + formatter.format(total_1) + ' total)',
          );
          $(api.column('month_2:name').footer()).html(
            formatter.format(pageTotal_2) + ' (' + formatter.format(total_2) + ' total)',
          );
        } catch (_e) {
          // Cost columns not present in the table yet — skip footer totals
        }
      },
    },
  );

  accountsDataTableToggles(tableId);
  addTableButtons();

  $(`select[name="${tableId}_fields"]`).on('hide.bs.select', function () {
    loadAccountsData(tableId, null, true);
  });

  rowDetailsClickHandler({ tableId: tableId, rowDetailCallback: formatAccountRow });
  loadAccountsData(tableId);
}

const addTableButtons = () => {
  const permissions = localStorage.permissions ? localStorage.permissions : false;
  const dt = $('#table-accounts').DataTable({ retrieve: true });
  let buttons = 0; // Add buttons before the already existing buttons

  if (checkValueAgainstList('manage_accounts', permissions)) {
    dt.button().add(
      buttons,
      accountsDataTableButtonAction(
        'accounts-update',
        'Trigger Account Updates',
        'Collect account information like the alternate contacts or the support level.',
      ),
    );
    buttons++;
  }

  if (checkValueAgainstList('manage_organizations', permissions)) {
    dt.button().add(
      buttons,
      accountsDataTableButtonAction(
        'organizations-update',
        'Trigger Organizational Updates',
        'Collect all AWS accounts from AWS Organizations.',
      ),
    );
  }
};

const createActionsButtons = (td, _cellData, rowData, _row, _col) => {
  const buttons = $('<div class="table-action-button-group">').appendTo(td);

  buttons.append(
    $('<button class="btn-datatable" data-bs-toggle="tooltip" title="Account Details">')
      .append('<i class="fas fa-external-link-alt">')
      .on('click', () => {
        window.open('?account_id=' + rowData.account_id + '#accountdetails', '_blank');
      }),
  );
};

export async function formatAccountRow(row) {
  const data = row.data();
  const aam = fetchAccountAccessLoginDetails(data.account_id);
  // Sign-in URLs already generated, so a re-render of the tabs does not need to request them again
  const idcLinkUrls = {};

  Promise.all([
    baseApiAxios.getAccount(data.account_id),
    baseApiAxios.getAccountPermissions(data.account_id),
    aam.visible,
  ])
    .then(response => {
      const account_details = { ...response[0], ...response[1].permanent_roles };
      const temp_access_roles = response[1].temporary_roles || [];
      const aamVisible = response[2];

      const renderTabs = (aamLoginDetails, aamLoading) => {
        row
          .child(
            <AccountTabs
              data={account_details}
              tempAccessRoles={temp_access_roles}
              isAccountDetailsPage={false}
              aamLoginDetails={aamLoginDetails}
              aamLoading={aamLoading}
            />,
          )
          .show();
        row.child()[0].setAttribute('class', 'rowDetails');
      };
      renderTabs(null, aamVisible);

      if (aamVisible) {
        aam.details
          .then(aamLoginDetails => {
            // The row may have been collapsed in the meantime
            if (!row.child.isShown()) return;
            if (aamLoginDetails) {
              renderTabs(aamLoginDetails, false);
              restoreIdcSignInLinks(idcLinkUrls);
            } else {
              $(row.child()).find('[name="aam-login-loading"]').remove();
            }
          })
          .catch(err => console.error('Failed to render the Account Access Manager login details', err));
      }
      return account_details;
    })
    .then(async account_details => {
      // Safely access idc_user_roles (may be missing or empty)
      const idcRoles = account_details?.idc_user_roles || [];

      if (idcRoles.length === 0) {
        console.log('No IDC user roles found — skipping link generation');
        return;
      }

      console.log(`Generating links for ${idcRoles.length} roles...`);

      // Fetch all links concurrently
      const linkPromises = idcRoles.map(async role => {
        try {
          // Use attribute selector to update all matching <a> elements (Account tab + CLI Config tab)
          const $links = $(`a[id="${role}"]`);
          $links.text('Generating Sign-In URL....Please wait..');

          const url = await createSwitchIdcRoleLink(
            role,
            account_details.account_id,
            ssoStartUrls[account_details.master_account],
          );
          idcLinkUrls[role] = url;

          // Update all <a> tags whose ID matches the role (queried again, the tabs may have been re-rendered)
          const $linksToUpdate = $(`a[id="${role}"]`);
          $linksToUpdate.attr('href', url);
          $linksToUpdate.text('Switch Role');
          console.log(`Added link for ${role}`);
        } catch (err) {
          $(`a[id="${role}"]`).text('Failed to generate Sign-In URL');
          console.error(`Failed to create link for ${role}:`, err);
        }
      });

      await Promise.all(linkPromises); // wait for all link updates to finish
    })
    .catch(err => {
      showErrorFromApiOperation('Failed to fetch account details')(err);
    })
    .finally(() => {
      removeSpinners();
    });
}

export function formatChildRowListAccountsHeader(data, infoContainer) {
  // Warning if account is marked for deletion
  listAccountsChildRowAddWarning(infoContainer, data);

  // Orders related to account actions, like 'account-close'
  listAccountsChildRowAddOrders(infoContainer, data);
}

export function formatChildRowListAccountsLeftSide(data, container) {
  // LEFT - ACCOUNT DETAILS
  const accountContainer = document.createElement('div');
  accountContainer.setAttribute('class', 'detailsContent');
  container.appendChild(accountContainer);

  const header = document.createElement('h4');
  header.innerText = 'AWS Account Details';
  accountContainer.appendChild(header);

  const accountContainerDetails = document.createElement('div');
  accountContainer.appendChild(accountContainerDetails);

  let fields = [];
  fields = fields.concat(accountInfoFields);
  fields = fields.concat(accountOrgInfoFields);

  createAndAddInfoTable(data, fields, accountContainerDetails);
}

function listAccountsChildRowAddWarning(container, data) {
  if (data.marked_for_deletion && data.fpc_status !== 'deleted') {
    const info = document.createElement('div');
    info.setAttribute('class', 'detailsContent');
    container.appendChild(info);

    const msg = document.createElement('div');
    msg.setAttribute('style', 'color: black;');
    msg.innerHTML = '<h3><strong>Warning:</strong> AWS Account is marked for deletion!</h3>';
    info.appendChild(msg);
  }
}

function listAccountsChildRowAddOrders(container, data) {
  if (data.orders && Object.getOwnPropertyNames(data.orders).length > 0) {
    const ordersContainer = document.createElement('div');
    ordersContainer.setAttribute('class', 'detailsContent');
    container.appendChild(ordersContainer);

    const ordersTable = document.createElement('table');
    ordersTable.setAttribute('class', 'table table-hover dataTable row-border nowrap compressed');
    ordersContainer.appendChild(ordersTable);

    const orderColumns = [
      { Name: 'Order Id', Value: 'id' },
      { Name: 'Action', Value: 'action' },
      { Name: 'Proposer', Value: 'proposer' },
      { Name: 'Creation Date', Value: 'creation_date' },
    ];

    const ordersTableHead = document.createElement('thead');
    ordersTable.appendChild(ordersTableHead);

    orderColumns.forEach(function (item) {
      const th = document.createElement('th');
      th.setAttribute('class', 'compressed');
      th.innerText = item['Name'];
      ordersTableHead.appendChild(th);
    });

    const ordersTableBody = document.createElement('tbody');
    ordersTable.appendChild(ordersTableBody);

    for (let key in data.orders) {
      const tr = document.createElement('tr');
      tr.setAttribute('role', 'row');
      tr.setAttribute('class', 'compressed');

      orderColumns.forEach(function (item) {
        const td = document.createElement('td');

        if (item['Value'] === 'id') {
          td.innerText = key;
          addCopyButton(td);
          addLoadOrderDetailsButton(td);
        } else {
          td.innerText = data.orders[key][item['Value']];
          addCopyButton(td);
        }

        tr.appendChild(td);
      });

      ordersTableBody.appendChild(tr);
    }
  }
}
